import type { ApiClient } from '../http/apiClient';
import type { IdentityProvider } from '../../auth/identityProvider';
import { buildMetaInfo } from '../model/metaInfo';
import type {
  AccountEntry,
  ValidateTeamAccountRequest,
  ValidateTeamAccountResponse,
  StartTeamOnboardingRequest,
  StartTeamOnboardingResponse,
  GetTeamOnboardingStatusRequest,
  GetTeamOnboardingStatusResponse,
  ListOnboardedTeamsRequest,
  ListOnboardedTeamsResponse,
  CheckTeamAccessRequest,
  CheckTeamAccessResponse,
  ListOwnerBindlesRequest,
  ListOwnerBindlesResponse,
} from '../model/types';

/**
 * TeamOnboarding client — one method per deployed team-onboarding operation
 * (ValidateTeamAccount, StartTeamOnboarding, GetTeamOnboardingStatus). Paths
 * mirror model/teamonboarding/operations.smithy exactly.
 *
 * Multi-tenant onboarding: a team grants Vector a cross-account IAM role (the
 * `VectorAccess-DO-NOT-DELETE` convention) per account; validate-account checks
 * a single granted role, start kicks off onboarding for a bindle + accounts,
 * and status reads the recorded onboarding record (its `accounts` field is a
 * JSON *string* — {@link TeamOnboardingApi.parseAccounts} parses it).
 *
 * Calls {@link ApiClient} only. Pure TS — no React.
 *
 * metaInfo: jobCategory is DOCUMENT_MANAGEMENT and requestType is the
 * KNOWN-VALID contextual value LIST_META_INFO — the backend RequestType enum
 * has no onboarding-specific values and rejects unknown enums with a 500, so we
 * reuse an accepted value rather than inventing one. requestType is contextual
 * only and does not affect onboarding behavior.
 */

const PATHS = {
  validateAccount: '/team-onboarding/validate-account',
  start: '/team-onboarding/start',
  status: '/team-onboarding/status',
  list: '/team-onboarding/list',
  checkAccess: '/team-onboarding/check-access',
  listBindles: '/team-onboarding/list-bindles',
} as const;

export interface ValidateAccountArgs {
  accountId: string;
  roleArn: string;
  bindleId?: string;
}

export interface StartTeamOnboardingArgs {
  teamName: string;
  bindleId: string;
  accounts: AccountEntry[];
}

export interface GetStatusArgs {
  bindleId: string;
}

export interface CheckTeamAccessArgs {
  bindleId: string;
}

export interface ListOwnerBindlesArgs {
  /** The abacus team id (amzn1.abacus.team.<id>) whose owned Bindles to list. */
  teamId: string;
}

/**
 * A parsed owned Bindle (from the ListOwnerBindles `bindles` JSON array
 * string): the Bindle's id and display name, as returned by Bindle
 * FindBindlesByOwner.
 */
export interface OwnedBindle {
  bindleId: string;
  bindleName: string;
}

/**
 * A parsed onboarded team (from the ListOnboardedTeams `teams` JSON array
 * string). One entry per onboarding record: the team's Bindle id, its display
 * name, the recorded onboarding status, and the count of validated accounts.
 */
export interface OnboardedTeam {
  bindleId: string;
  teamName: string;
  /** The recorded onboarding status, when present. */
  status?: string;
  /** Count of validated accounts, when recorded. */
  accountCount?: number;
}

/**
 * A parsed onboarding account (from the status response's `accounts` JSON
 * string). Carries the granted cross-account entry plus best-effort
 * validation / onboarding flags the backend records.
 */
export interface OnboardingAccount {
  accountId: string;
  stage?: string;
  region?: string;
  roleArn?: string;
  /** Whether the granted role validated successfully, when recorded. */
  valid?: boolean;
  /** Whether the account finished onboarding, when recorded. */
  onboarded?: boolean;
  /** Validation error message, when the backend recorded one. */
  errorMessage?: string;
}

export class TeamOnboardingApi {
  constructor(
    private readonly apiClient: ApiClient,
    private readonly identityProvider: IdentityProvider,
  ) {}

  /**
   * Parses the `accounts` JSON string of a GetTeamOnboardingStatus response
   * into a normalized {@link OnboardingAccount} list. Tolerates both a bare
   * array and an object with an `accounts` array, and unknown/missing fields.
   */
  static parseAccounts(accounts?: string): OnboardingAccount[] {
    if (!accounts) return [];
    let raw: unknown;
    try {
      raw = JSON.parse(accounts);
    } catch {
      return [];
    }
    const list = Array.isArray(raw)
      ? raw
      : Array.isArray((raw as Record<string, unknown>)?.accounts)
        ? ((raw as Record<string, unknown>).accounts as unknown[])
        : [];
    return list.map((a) => {
      const o = (a ?? {}) as Record<string, unknown>;
      return {
        accountId:
          (typeof o.accountId === 'string' && o.accountId) ||
          (typeof o.account === 'string' && o.account) ||
          '',
        ...(typeof o.stage === 'string' ? { stage: o.stage } : {}),
        ...(typeof o.region === 'string' ? { region: o.region } : {}),
        ...(typeof o.roleArn === 'string' ? { roleArn: o.roleArn } : {}),
        ...(typeof o.valid === 'boolean' ? { valid: o.valid } : {}),
        ...(typeof o.onboarded === 'boolean' ? { onboarded: o.onboarded } : {}),
        ...(typeof o.errorMessage === 'string'
          ? { errorMessage: o.errorMessage }
          : {}),
      };
    });
  }

  /**
   * Parses the `teams` JSON array string of a ListOnboardedTeams response into
   * a normalized {@link OnboardedTeam} list. Tolerates a bare array or an object
   * with a `teams` array, unknown/missing fields, and a numeric or numeric
   * string `accountCount`.
   */
  static parseTeams(teams?: string): OnboardedTeam[] {
    if (!teams) return [];
    let raw: unknown;
    try {
      raw = JSON.parse(teams);
    } catch {
      return [];
    }
    const list = Array.isArray(raw)
      ? raw
      : Array.isArray((raw as Record<string, unknown>)?.teams)
        ? ((raw as Record<string, unknown>).teams as unknown[])
        : [];
    return list
      .map((t) => {
        const o = (t ?? {}) as Record<string, unknown>;
        const count =
          typeof o.accountCount === 'number'
            ? o.accountCount
            : typeof o.accountCount === 'string' &&
                o.accountCount.trim() !== '' &&
                Number.isFinite(Number(o.accountCount))
              ? Number(o.accountCount)
              : undefined;
        return {
          bindleId: typeof o.bindleId === 'string' ? o.bindleId : '',
          teamName:
            (typeof o.teamName === 'string' && o.teamName) ||
            (typeof o.name === 'string' && o.name) ||
            '',
          ...(typeof o.status === 'string' ? { status: o.status } : {}),
          ...(count !== undefined ? { accountCount: count } : {}),
        };
      })
      .filter((t) => t.bindleId.length > 0);
  }

  /**
   * Parses the `bindles` JSON array string of a ListOwnerBindles response into
   * a normalized {@link OwnedBindle} list. Tolerates a bare array or an object
   * with a `bindles` array, unknown/missing fields, and a `name` alias for the
   * bindle name; drops entries without a bindleId.
   */
  static parseBindles(bindles?: string): OwnedBindle[] {
    if (!bindles) return [];
    let raw: unknown;
    try {
      raw = JSON.parse(bindles);
    } catch {
      return [];
    }
    const list = Array.isArray(raw)
      ? raw
      : Array.isArray((raw as Record<string, unknown>)?.bindles)
        ? ((raw as Record<string, unknown>).bindles as unknown[])
        : [];
    return list
      .map((b) => {
        const o = (b ?? {}) as Record<string, unknown>;
        const bindleId = typeof o.bindleId === 'string' ? o.bindleId : '';
        return {
          bindleId,
          bindleName:
            (typeof o.bindleName === 'string' && o.bindleName) ||
            (typeof o.name === 'string' && o.name) ||
            bindleId,
        };
      })
      .filter((b) => b.bindleId.length > 0);
  }

  async validateAccount(
    args: ValidateAccountArgs,
  ): Promise<ValidateTeamAccountResponse> {
    const metaInfo = buildMetaInfo({
      identity: await this.identityProvider.getIdentity(),
      jobName: 'ValidateTeamAccount',
      jobCategory: 'DOCUMENT_MANAGEMENT',
      requestType: 'LIST_META_INFO',
    });
    return this.apiClient.post<
      ValidateTeamAccountRequest,
      ValidateTeamAccountResponse
    >(PATHS.validateAccount, {
      metaInfo,
      accountId: args.accountId,
      roleArn: args.roleArn,
      ...(args.bindleId ? { bindleId: args.bindleId } : {}),
    });
  }

  async startTeamOnboarding(
    args: StartTeamOnboardingArgs,
  ): Promise<StartTeamOnboardingResponse> {
    const metaInfo = buildMetaInfo({
      identity: await this.identityProvider.getIdentity(),
      jobName: 'StartTeamOnboarding',
      jobCategory: 'DOCUMENT_MANAGEMENT',
      requestType: 'LIST_META_INFO',
    });
    return this.apiClient.post<
      StartTeamOnboardingRequest,
      StartTeamOnboardingResponse
    >(PATHS.start, {
      metaInfo,
      teamName: args.teamName,
      bindleId: args.bindleId,
      accounts: args.accounts,
    });
  }

  async getStatus(
    args: GetStatusArgs,
  ): Promise<GetTeamOnboardingStatusResponse> {
    const metaInfo = buildMetaInfo({
      identity: await this.identityProvider.getIdentity(),
      jobName: 'GetTeamOnboardingStatus',
      jobCategory: 'DOCUMENT_MANAGEMENT',
      requestType: 'LIST_META_INFO',
    });
    return this.apiClient.post<
      GetTeamOnboardingStatusRequest,
      GetTeamOnboardingStatusResponse
    >(PATHS.status, {
      metaInfo,
      bindleId: args.bindleId,
    });
  }

  /**
   * Lists the onboarded teams, parsing the response's `teams` JSON array string
   * into a normalized {@link OnboardedTeam} list ([] when none).
   */
  async listOnboardedTeams(): Promise<OnboardedTeam[]> {
    const metaInfo = buildMetaInfo({
      identity: await this.identityProvider.getIdentity(),
      jobName: 'ListOnboardedTeams',
      jobCategory: 'DOCUMENT_MANAGEMENT',
      requestType: 'LIST_META_INFO',
    });
    const res = await this.apiClient.post<
      ListOnboardedTeamsRequest,
      ListOnboardedTeamsResponse
    >(PATHS.list, { metaInfo });
    return TeamOnboardingApi.parseTeams(res.teams);
  }

  /**
   * Authorizes the calling actor for a team identified by its Bindle id.
   * Returns the raw {@link CheckTeamAccessResponse} ({ authorized, reason }) so
   * callers can surface the reason when access is denied.
   */
  async checkTeamAccess(
    args: CheckTeamAccessArgs,
  ): Promise<CheckTeamAccessResponse> {
    const metaInfo = buildMetaInfo({
      identity: await this.identityProvider.getIdentity(),
      jobName: 'CheckTeamAccess',
      jobCategory: 'DOCUMENT_MANAGEMENT',
      requestType: 'LIST_META_INFO',
    });
    return this.apiClient.post<
      CheckTeamAccessRequest,
      CheckTeamAccessResponse
    >(PATHS.checkAccess, {
      metaInfo,
      bindleId: args.bindleId,
    });
  }

  /**
   * Lists the Bindles owned by the supplied abacus team id
   * (amzn1.abacus.team.<id>), parsing the response's `bindles` JSON array string
   * into a normalized {@link OwnedBindle} list ([] when none). The team id is
   * sent alongside metaInfo and used server-side as the FindBindlesByOwner
   * owner.
   */
  async listOwnerBindles(args: ListOwnerBindlesArgs): Promise<OwnedBindle[]> {
    const metaInfo = buildMetaInfo({
      identity: await this.identityProvider.getIdentity(),
      jobName: 'ListOwnerBindles',
      jobCategory: 'DOCUMENT_MANAGEMENT',
      requestType: 'LIST_META_INFO',
    });
    const res = await this.apiClient.post<
      ListOwnerBindlesRequest,
      ListOwnerBindlesResponse
    >(PATHS.listBindles, {
      metaInfo,
      teamId: args.teamId,
    });
    return TeamOnboardingApi.parseBindles(res.bindles);
  }
}
